/**
 * 日期相关的工具函数
 * 从 datalab 项目中提取的日期相关工具函数
 */

import moment, { Moment } from 'moment';
import {
  TDateRange,
  TDateGranularity,
  TRelativeTime,
  DateGranularity,
} from './types';
import {
  MomentFormatMap,
  MomentUnitMap,
  GranularityRelativeOptionsConfig,
} from './constants';

/**
 * 通用的相对时间范围计算方法
 * @param baseRange - 基准时间范围
 * @param amount - 时间偏移量
 * @param granularity - 时间跨度的粒度
 * @param timeGranularity - 当前基准时间的粒度
 * @returns 计算后的时间范围
 * TODO: getLastDateRange 和这个方法, 应用于不同的场景, 是否可以变为一个方法?
 */
export function calculateRelativeTimeRange(
  baseRange: TDateRange,
  amount: number | string,
  granularity: TDateGranularity,
  timeGranularity: TDateGranularity,
): TDateRange {
  const startMoment = moment(baseRange[0]);
  const endMoment = moment(baseRange[1]);
  // 用于计算跨度的粒度, 比如小时粒度时, 要按照秒来计算起止时间有多少秒, 月粒度, 要按照起止时间间隔多少个月来计算.
  const durationUnit = timeGranularity === 'hour' ? 'seconds' : timeGranularity;
  
  // Step 1: 根据startMoment和endMoment之间的跨度计算adjustedEndMoment
  const duration = endMoment.diff(startMoment, durationUnit);

  // Step 2: 计算对比时间的起始时间
  const adjustedStartMoment = startMoment.subtract(amount, granularity);
  
  // Step 3: 计算对比时间的结束时间
  const adjustedEndMoment = adjustedStartMoment.clone().add(duration, durationUnit);
  
  // 周粒度下, 开始时间要从周一开始, 终止时间为周日, 所以使用了startOf和endOf
  // hour粒度下, 会有精确到具体的秒, 所以不能使用startOf和endOf
  return [
    timeGranularity !== 'hour' ? adjustedStartMoment.startOf(MomentUnitMap[timeGranularity]) : adjustedStartMoment,
    timeGranularity !== 'hour' ? adjustedEndMoment.endOf(MomentUnitMap[timeGranularity]) : adjustedEndMoment,
  ];
}

/**
 * 计算上一期时间范围
 * @param baseRange - 基准时间范围
 * @param granularity - 时间跨度的粒度
 * @returns 上一期时间范围
 */
export function calculatePreviousPeriod(baseRange: TDateRange, granularity: TDateGranularity): TDateRange {
  const startMoment = moment(baseRange[0]);
  const endMoment = moment(baseRange[1]);

  // 用于计算跨度的粒度, 比如小时粒度时, 要按照秒来计算起止时间有多少秒, 月粒度, 要按照起止时间间隔多少个月来计算.
  const durationUnit = granularity === DateGranularity.HOUR ? 'seconds' : granularity;
  const duration = endMoment.diff(startMoment, durationUnit);
  
  if (granularity === DateGranularity.MONTH) {
    // 月份之间的天数不同, 需要endOf计算到月份的最后一天日期.
    return [
      startMoment.subtract(duration + 1, durationUnit).startOf('month'),
      endMoment.subtract(duration + 1, durationUnit).endOf('month')
    ];
  }
  
  return [
    startMoment.subtract(duration + 1, durationUnit),
    endMoment.subtract(duration + 1, durationUnit)
  ];
}

/**
 * 获取对日期进行粒度对应的格式化后的字符串
 * @param date
 * @param granularity
 */
export const formatDate = (date: Moment | string, granularity: TDateGranularity): string => {
  const momentDate = moment(date);
  return momentDate.format(MomentFormatMap[granularity] || 'YYYY-MM-DD');
};

/**
 * 获取格式化时间范围的字符串
 * @param timeRange - 时间范围
 * @param granularity - 时间粒度
 * @returns 拼接日期后显示的字符串
 */
export function joinDate(timeRange: TDateRange, granularity: TDateGranularity): string {
  const [startDate, endDate] = Array.isArray(timeRange) ? timeRange : [moment(), moment()];
  return `${formatDate(startDate, granularity)} ~ ${formatDate(endDate, granularity)}`;
}

export const getStartOrEndDate = (date: string | Moment, config): Moment => {
  const { granularity, isStart=true, unit } = config;
  const momentDate = typeof date === "string" ? moment(date) : date;
  const unitStr = unit || MomentUnitMap[granularity] || 'days';
  if (isStart) {
    return momentDate?.startOf(unitStr);
  }

  return momentDate?.startOf(unit).endOf(unitStr);
};

/**
 * 时间上传参数中, value 或 time_range
 * hour格式 YYYY-MM-DD HH:mm:ss, 且不能用startOf等方法
 * day, week, month, 格式需要时YYYY-MM-DD, 且需要获取周/月第一天 和 周/月 最后一天
 * @param timeRange
 * @param granularity
 */
export function formatDateRange(timeRange: TDateRange, granularity: TDateGranularity): [string, string] {
  const [startDate, endDate] = timeRange || [];
  if (granularity === 'hour') {
    return [formatDate(startDate, granularity), formatDate(endDate, granularity)];
  }
  const start = moment(startDate).startOf(MomentUnitMap[granularity]).format('YYYY-MM-DD');
  const end = moment(endDate).endOf(MomentUnitMap[granularity]).format('YYYY-MM-DD');
  return [start, end];
}

/**
 * 解析一些  '1-day', '2-week' 这样的value值,
 * @param value
 */
export function parseRelativeDateStr(value: string | null): { amount: string; granularity: TDateGranularity; isSelectedCustom: boolean; } {
  const result = value?.split('-') || ['0', 'day'];
  return { amount: result[0], granularity: result[1] as TDateGranularity, isSelectedCustom: !!result[2] };
}

/**
 * 根据相对选项值计算对应的时间范围
 * @param baseRange - 基准时间范围
 * @param config - 配置项
 *  amount 是时间跨度
 *  granularity 是时间跨度的粒度 比如 当前是 日粒度, 但是 跨度是 1 week ago, 那么granularity 是 week
 *  timeGranularity 是当前的时间粒度, 当前是月粒度, 那么 timeGranularity 就是 month
 * @returns 计算后的时间范围 展示字符串
 */
export function getDateRange(baseRange: TDateRange, config: {amount: string; granularity: TDateGranularity; timeGranularity: TDateGranularity;}): TDateRange {
  const { amount, granularity, timeGranularity } = config;
  if (amount === '0') {
    return calculatePreviousPeriod(baseRange, timeGranularity);
  }
  return calculateRelativeTimeRange(baseRange, amount, granularity, timeGranularity);
}

/**
 * 生成「上期」默认对比项的受控值
 */
export function getPreviousPeriodValue(
  _baseRange: TDateRange,
  granularity: TDateGranularity,
): TRelativeTime {
  return {
    name_cn: '上期',
    name_en: 'Previous period',
    last_time: '0',
    granularity,
    is_custom: false,
  };
}

export const getRelativeDateRangText = (props: {
  baseTimeRange: TDateRange;
  amount: string;
  granularity: TDateGranularity;
  timeGranularity: TDateGranularity;
  isSelectedCustom: boolean;
  isEn: boolean;
}): { prefix: string; timeRangeStr: string } => {
  const {
    baseTimeRange,
    amount,
    granularity,
    timeGranularity,
    isSelectedCustom,
    isEn,
  } = props;

  const range = getDateRange(baseTimeRange, {
    amount,
    granularity,
    timeGranularity,
  });

  const timeRangeStr = joinDate(range, timeGranularity);
  let prefix: string;
  if (isSelectedCustom) {
    prefix = isEn ? 'Custom' : '自定义';
  } else {
    const relativeOptions =
      GranularityRelativeOptionsConfig[timeGranularity] || [];
    const option = relativeOptions.find(
      opt => opt.amount === amount && opt.granularity === granularity,
    );
    const textEn = option?.textEn ?? '';
    const textCn = option?.textCn ?? '';
    prefix = isEn ? textEn : textCn;
  }

  return { prefix, timeRangeStr };
};
